// Import model
import { DataView } from '../models/DataView';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { Field } from '../models/Field';
import { Form } from '../models/Form';
import { Menu } from '../models/Menu';

// Import dependency serializers (for complex types)
import { FieldSerializer } from './FieldSerializer';
import { FormSerializer } from './FormSerializer';
import { MenuSerializer } from './MenuSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for DataView - handles JSON serialization and deserialization with type validation
 */
export class DataViewSerializer {
  /**
   * Deserializes a JSON string into a DataView instance
   */
  static deserialize(json: string): DataView {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a DataView instance
   */
  static fromObject(obj: any): DataView {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to DataView');
    }


    const instance: DataView = {
      defaultSort: obj['DefaultSort'] as string | undefined,
      fields: obj['Fields'] ? (obj['Fields'] as any[]).map((item: any) =>
        FieldSerializer.fromObject(item)
      ) : [],
      filter: obj['Filter'] ? (obj['Filter'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      filterForm: obj['FilterForm'] ? FormSerializer.fromObject(obj['FilterForm']) : undefined as any,
      isFilterableLocally: obj['IsFilterableLocally'] as boolean | undefined,
      isSelectable: obj['IsSelectable'] as boolean | undefined,
      keyField: obj['KeyField'] as string | undefined,
      menu: obj['Menu'] ? MenuSerializer.fromObject(obj['Menu']) : undefined as any,
      name: obj['Name'] as string,
      pageSize: obj['PageSize'] as number | undefined,
      searchable: obj['Searchable'] as boolean | undefined,
      title: obj['Title'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of DataView instances
   */
  static deserializeArray(json: string): DataView[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a DataView instance to a JSON string
   */
  static serialize(instance: DataView): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a DataView instance to a plain object
   */
  static toObject(instance: DataView): any {
    return {
      'DefaultSort': instance.defaultSort,
      'Fields': instance.fields?.map((item: any) =>
        FieldSerializer.toObject(item)
      ),
      'Filter': instance.filter?.map((item: any) =>
        item
      ),
      'FilterForm': instance.filterForm ? FormSerializer.toObject(instance.filterForm) : undefined,
      'IsFilterableLocally': instance.isFilterableLocally,
      'IsSelectable': instance.isSelectable,
      'KeyField': instance.keyField,
      'Menu': instance.menu ? MenuSerializer.toObject(instance.menu) : undefined,
      'Name': instance.name,
      'PageSize': instance.pageSize,
      'Searchable': instance.searchable,
      'Title': instance.title,
    };
  }

  /**
   * Serializes an array of DataView instances to a JSON string
   */
  static serializeArray(instances: DataView[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
